"use client";

import { useCallback, useState } from "react";
import { ArrowLeft, MailCheck } from "@/components/icons";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { Button } from "@/components/ui/button";
import { inputVariants } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  MAIN_PRODUCT_TYPE_SLUGS,
  getProductTypeBySlug,
} from "@/lib/generated/product-types";
import { cn } from "@/lib/utils";
import { PublicListingBasics } from "@/components/product/public-listing-basics";
import { useVendorDraftFlow } from "@/components/product/use-vendor-draft-flow";
import { type DeliveryLocation } from "@/lib/checkout/delivery-location";
import { PublicListingContactFields } from "@/components/product/public-listing-contact-fields";
import type { ContactInfo, Photo } from "@/lib/products/types";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface TradeListingFormProps {
  intent: "sell" | "recycle";
  productSlug?: string;
}

export function TradeListingForm({
  intent,
  productSlug,
}: TradeListingFormProps) {
  const [phase, setPhase] = useState<"capture" | "contact" | "sent">("capture");
  const [selectedSlug, setSelectedSlug] = useState("");
  const effectiveSlug = productSlug ?? selectedSlug;
  const [quantity, setQuantity] = useState(0);
  const [location, setLocation] = useState<DeliveryLocation>({
    zip: "",
    city: "",
    state: "",
  });
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [contact, setContact] = useState<ContactInfo>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const {
    attestUpload,
    submit,
    isSubmitting,
    errors,
    setErrors,
    generateUploadUrl,
  } = useVendorDraftFlow();
  const contactValid =
    contact.firstName.trim().length > 0 &&
    contact.lastName.trim().length > 0 &&
    EMAIL_PATTERN.test(contact.email.trim());

  const handleSubmit = useCallback(async () => {
    const sent = await submit({
      intent,
      productTypeSlug: effectiveSlug,
      quantityAvailable: quantity,
      location: {
        line1: location.line1,
        city: location.city,
        state: location.state,
        postalCode: location.zip,
      },
      photos,
      ...contact,
      companyName: contact.companyName || undefined,
    });
    if (sent) setPhase("sent");
  }, [submit, intent, effectiveSlug, quantity, location, photos, contact]);

  if (phase === "sent") return <SentCard email={contact.email.trim()} />;
  if (phase === "contact") {
    return (
      <ContactCard
        intent={intent}
        contact={contact}
        onChange={(updates) =>
          setContact((current) => ({ ...current, ...updates }))
        }
        errors={errors}
        onDismissErrors={() => setErrors([])}
        canSubmit={contactValid}
        isSubmitting={isSubmitting}
        onBack={() => setPhase("capture")}
        onSubmit={handleSubmit}
      />
    );
  }
  return (
    <PublicListingBasics
      heading={
        <h2 className="text-lg font-semibold text-foreground">Get started</h2>
      }
      productSelect={
        productSlug === undefined ? (
          <ProductTypeSelectField
            productSelect={{ value: selectedSlug, onChange: setSelectedSlug }}
          />
        ) : undefined
      }
      productSlug={effectiveSlug}
      quantity={quantity}
      location={location}
      photos={photos}
      onQuantityChange={setQuantity}
      setLocation={setLocation}
      onPhotosChange={setPhotos}
      attestUpload={attestUpload}
      generateUploadUrl={generateUploadUrl}
      onContinue={() => setPhase("contact")}
    />
  );
}

interface ProductSelectState {
  value: string;
  onChange: (slug: string) => void;
}

function ProductTypeSelectField({
  productSelect,
}: {
  productSelect?: ProductSelectState;
}) {
  if (!productSelect) return null;
  return (
    <div className="space-y-1.5">
      <Label htmlFor="quick-product">What are you listing?</Label>
      <select
        id="quick-product"
        className={cn(inputVariants(), "appearance-none")}
        value={productSelect.value}
        onChange={(event) => productSelect.onChange(event.target.value)}
      >
        <option value="" disabled>
          Choose a packaging type
        </option>
        {MAIN_PRODUCT_TYPE_SLUGS.map((slug) => (
          <option key={slug} value={slug}>
            {getProductTypeBySlug(slug)?.pluralDisplayName ?? slug}
          </option>
        ))}
      </select>
    </div>
  );
}

interface ContactCardProps {
  intent: "sell" | "recycle";
  contact: ContactInfo;
  onChange: (updates: Partial<ContactInfo>) => void;
  errors: string[];
  onDismissErrors: () => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
}

function ContactCard({
  intent,
  contact,
  onChange,
  errors,
  onDismissErrors,
  canSubmit,
  isSubmitting,
  onBack,
  onSubmit,
}: ContactCardProps) {
  return (
    <>
      <h2 className="text-lg font-semibold text-foreground">
        Save your listing and confirm your email
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Open the link we email you to finish your listing.
      </p>
      <div className="mt-5">
        <PublicListingContactFields
          intent={intent}
          contact={contact}
          onChange={onChange}
        />
      </div>
      {errors.length > 0 && (
        <ActionErrorAlert
          className="mt-4"
          error={errors.join(" • ")}
          onDismiss={onDismissErrors}
        />
      )}
      <ContactCardActions
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
        onBack={onBack}
        onSubmit={onSubmit}
      />
    </>
  );
}

function ContactCardActions({
  canSubmit,
  isSubmitting,
  onBack,
  onSubmit,
}: Pick<ContactCardProps, "canSubmit" | "isSubmitting" | "onBack" | "onSubmit">) {
  return (
    <>
      <Button
        variant="primary"
        size="default"
        className="mt-6 w-full"
        disabled={!canSubmit || isSubmitting}
        onClick={onSubmit}
      >
        {isSubmitting ? "Sending…" : "Email me a link"}
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back
      </button>
    </>
  );
}

function SentCard({ email }: { email: string }) {
  return (
    <div className="flex flex-col items-center py-6 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-success-bg text-success-strong">
        <MailCheck className="size-7" />
      </div>
      <h2 className="mt-5 text-lg font-semibold text-foreground">
        Check your email
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        We sent a link to <span className="font-medium">{email}</span>. Open it
        to finish your listing and submit it for review. Your listing is not
        submitted yet.
      </p>
    </div>
  );
}
